import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Tabs, Tab, TextField, Button,
  Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Checkbox, Chip, Alert, CircularProgress, Pagination,
  Card, CardContent, CardMedia, InputAdornment, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress,
  Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import {
  Search as SearchIcon,
  CloudUpload as UploadIcon,
  Download as ImportIcon,
  Store as StoreIcon,
  ShoppingCart as ShopeeIcon,
  CheckCircle as SuccessIcon,
  Image as ImageIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

const ImportProducts = () => {
  const { t } = useTranslation('admin');
  const [activeTab, setActiveTab] = useState(0);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
        {t('import.title')}
      </Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab icon={<StoreIcon />} label={t('import.ezp_tab')} />
          <Tab icon={<ShopeeIcon />} label={t('import.shopee_tab')} />
        </Tabs>
      </Paper>

      {activeTab === 0 && <EzpImport />}
      {activeTab === 1 && <ShopeeImport />}
    </Box>
  );
};

// ==================== 好批上架 ====================
const EzpImport = () => {
  const { t } = useTranslation('admin');
  const [search, setSearch] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selected, setSelected] = useState([]);
  const [priceRate, setPriceRate] = useState(1.4);
  const [importing, setImporting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [targetCategory, setTargetCategory] = useState('');

  // 載入本店分類，預設選中「新品上架」
  useEffect(() => {
    api.get('/settings/categories/public').then(res => {
      if (res.data.success) {
        const cats = res.data.categories || [];
        setCategories(cats);
        const defaultCat = cats.find(c => c.name === '新品上架') || cats.find(c => c.name !== '未分類');
        if (defaultCat) setTargetCategory(String(defaultCat.id));
      }
    }).catch(() => {});
  }, []);

  const searchProducts = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const res = await api.get('/admin/import/ezp/products', {
        params: { search, page: p, limit: 20 }
      });
      if (res.data.success) {
        setProducts(res.data.items);
        setTotal(res.data.total);
        setTotalPages(res.data.totalPages);
        setPage(p);
      }
    } catch (error) {
      toast.error(t('import.search_failed') + ': ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  }, [search]);

  const handleSearch = () => {
    setSelected([]);
    searchProducts(1);
  };

  const handleToggle = (goodsId) => {
    setSelected(prev =>
      prev.includes(goodsId) ? prev.filter(id => id !== goodsId) : [...prev, goodsId]
    );
  };

  const handleSelectAll = () => {
    if (selected.length === products.length) {
      setSelected([]);
    } else {
      setSelected(products.map(p => p.goods_id));
    }
  };

  const handleImport = async () => {
    if (selected.length === 0) {
      toast.warning(t('import.select_products_first'));
      return;
    }

    setImporting(true);
    try {
      const res = await api.post('/admin/import/ezp/import', {
        goods_ids: selected,
        price_rate: priceRate,
        category_id: targetCategory || null
      });
      if (res.data.success) {
        toast.success(res.data.message);
        setSelected([]);
        searchProducts(page); // 重新搜尋更新狀態
      }
    } catch (error) {
      toast.error(t('import.import_failed') + ': ' + (error.response?.data?.error || error.message));
    } finally {
      setImporting(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Alert severity="info" sx={{ mb: 3 }}>
        {t('import.ezp_desc')}
      </Alert>

      {/* Search bar */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={5}>
          <TextField
            fullWidth
            placeholder={t('import.search_placeholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
            }}
          />
        </Grid>
        <Grid item xs={6} sm={2}>
          <TextField
            fullWidth
            type="number"
            label={t('import.price_rate')}
            value={priceRate}
            onChange={(e) => setPriceRate(parseFloat(e.target.value) || 1)}
            InputProps={{ inputProps: { min: 1, max: 5, step: 0.1 } }}
            helperText={`批價 ×${priceRate}`}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <FormControl fullWidth>
            <InputLabel>{t('import.target_category')}</InputLabel>
            <Select value={targetCategory} onChange={(e) => setTargetCategory(e.target.value)} label={t('import.target_category')}>
              <MenuItem value="">{t('import.no_specify')}</MenuItem>
              {categories.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={2}>
          <Button fullWidth variant="contained" onClick={handleSearch} disabled={loading} startIcon={loading ? <CircularProgress size={18} /> : <SearchIcon />} sx={{ height: 56 }}>
            {t('common.search')}
          </Button>
        </Grid>
      </Grid>

      {/* Results */}
      {products.length > 0 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('import.total_selected', { total, selected: selected.length })}
            </Typography>
            <Button
              variant="contained"
              color="success"
              startIcon={importing ? <CircularProgress size={18} /> : <ImportIcon />}
              onClick={handleImport}
              disabled={importing || selected.length === 0}
            >
              {t('import.import_selected', { count: selected.length })}
            </Button>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox checked={selected.length === products.length && products.length > 0} onChange={handleSelectAll} />
                  </TableCell>
                  <TableCell>{t('import.image')}</TableCell>
                  <TableCell>{t('import.product_name')}</TableCell>
                  <TableCell>{t('import.sku')}</TableCell>
                  <TableCell align="right">{t('import.wholesale_price')}</TableCell>
                  <TableCell align="right">{t('import.selling_price')} (x{priceRate})</TableCell>
                  <TableCell align="right">{t('import.stock')}</TableCell>
                  <TableCell>{t('import.category')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map(p => (
                  <TableRow key={p.goods_id} hover selected={selected.includes(p.goods_id)} onClick={() => handleToggle(p.goods_id)} sx={{ cursor: 'pointer' }}>
                    <TableCell padding="checkbox">
                      <Checkbox checked={selected.includes(p.goods_id)} />
                    </TableCell>
                    <TableCell>
                      {p.image_url ? (
                        <Box component="img" src={p.image_url} alt="" sx={{ width: 50, height: 50, objectFit: 'contain', borderRadius: 1, border: '1px solid #eee' }} />
                      ) : <ImageIcon color="disabled" />}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.goods_name}
                      </Typography>
                    </TableCell>
                    <TableCell><Typography variant="caption" color="text.secondary">{p.goods_sn}</Typography></TableCell>
                    <TableCell align="right"><Typography variant="body2" color="text.secondary">${p.shop_price}</Typography></TableCell>
                    <TableCell align="right"><Typography variant="body2" fontWeight="bold" color="error">${Math.round(p.shop_price * priceRate)}</Typography></TableCell>
                    <TableCell align="right">{p.goods_number}</TableCell>
                    <TableCell><Chip label={p.cat_name || '-'} size="small" variant="outlined" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination count={totalPages} page={page} onChange={(e, p) => searchProducts(p)} color="primary" />
            </Box>
          )}
        </>
      )}

      {!loading && products.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
          <StoreIcon sx={{ fontSize: 60, mb: 2, opacity: 0.3 }} />
          <Typography>{t('import.ezp_search_hint')}</Typography>
        </Box>
      )}
    </Paper>
  );
};

// ==================== Shopee Import ====================
const ShopeeImport = () => {
  const { t } = useTranslation('admin');

  const FILE_TYPE_LABELS = {
    basic_info: { label: t('import.file_basic_info'), color: 'primary' },
    sales_info: { label: t('import.file_sales_info'), color: 'success' },
    media_info: { label: t('import.file_media_info'), color: 'secondary' },
  };
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [importing, setImporting] = useState(false);
  const [priceAdjustment, setPriceAdjustment] = useState(1.0);
  const [defaultPrice, setDefaultPrice] = useState(0);
  const [defaultStock, setDefaultStock] = useState(0);
  const [categories, setCategories] = useState([]);
  const [targetCategory, setTargetCategory] = useState('');
  const [importResult, setImportResult] = useState(null);

  // 載入本店分類，預設選中「新品上架」
  useEffect(() => {
    api.get('/settings/categories/public').then(res => {
      if (res.data.success) {
        const cats = res.data.categories || [];
        setCategories(cats);
        const defaultCat = cats.find(c => c.name === '新品上架') || cats.find(c => c.name !== '未分類');
        if (defaultCat) setTargetCategory(String(defaultCat.id));
      }
    }).catch(() => {});
  }, []);

  const handleUpload = async (event) => {
    const fileList = event.target.files;
    if (!fileList || fileList.length === 0) return;

    setUploading(true);
    setPreview(null);
    setImportResult(null);

    try {
      const formData = new FormData();
      for (let i = 0; i < fileList.length; i++) {
        formData.append('files', fileList[i]);
      }

      const res = await api.post('/admin/import/shopee/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.data.success) {
        setPreview(res.data);
        setAllProducts(res.data.all_products || []);
        setSelected(res.data.all_products?.map((_, i) => i) || []);
        toast.success(t('import.parse_success', { count: res.data.valid_products }));
      }
    } catch (error) {
      toast.error(t('import.upload_failed') + ': ' + (error.response?.data?.error || error.message));
    } finally {
      setUploading(false);
      // 重置 input 讓同檔案可以再次選取
      event.target.value = '';
    }
  };

  const handleToggle = (idx) => {
    setSelected(prev => prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]);
  };

  const handleSelectAll = () => {
    if (selected.length === allProducts.length) {
      setSelected([]);
    } else {
      setSelected(allProducts.map((_, i) => i));
    }
  };

  const handleImport = async () => {
    if (selected.length === 0) {
      toast.warning(t('import.select_products_first'));
      return;
    }

    setImporting(true);
    try {
      const productsToImport = selected.map(idx => allProducts[idx]);
      const res = await api.post('/admin/import/shopee/execute', {
        products: productsToImport,
        category_id: targetCategory || null,
        price_adjustment: priceAdjustment,
        default_price: defaultPrice,
        default_stock: defaultStock
      });
      if (res.data.success) {
        toast.success(res.data.message);
        setImportResult(res.data);
      }
    } catch (error) {
      toast.error(t('import.import_failed') + ': ' + (error.response?.data?.error || error.message));
    } finally {
      setImporting(false);
    }
  };

  const handleReset = () => {
    setPreview(null);
    setAllProducts([]);
    setSelected([]);
    setImportResult(null);
    setDefaultPrice(0);
    setDefaultStock(0);
  };

  // 計算顯示用價格（有價格用調整後，無價格用預設）
  const getDisplayPrice = (p) => {
    const raw = parseFloat(p.price) || 0;
    if (raw > 0) return Math.round(raw * priceAdjustment);
    return parseFloat(defaultPrice) || 0;
  };

  const getDisplayStock = (p) => {
    const raw = parseInt(p.quantity) || 0;
    if (raw > 0) return raw;
    return parseInt(defaultStock) || 0;
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Alert severity="info" sx={{ mb: 3 }}>
        {t('import.shopee_desc')}
        <Box component="ul" sx={{ mb: 0, mt: 0.5 }}>
          <li><strong>{t('import.shopee_file_basic')}</strong> — {t('import.shopee_file_basic_desc')}</li>
          <li><strong>{t('import.shopee_file_price')}</strong> — {t('import.shopee_file_price_desc')}</li>
          <li><strong>{t('import.shopee_file_media')}</strong> — {t('import.shopee_file_media_desc')}</li>
        </Box>
        {t('import.shopee_download_hint')}
      </Alert>

      {importResult ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <SuccessIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>{t('import.import_complete')}</Typography>
          <Typography variant="body1">
            {t('import.import_success_count', { count: importResult.imported })}
            {importResult.skipped > 0 && ` ${t('import.import_skipped', { count: importResult.skipped })}`}
          </Typography>
          <Button variant="contained" onClick={handleReset} startIcon={<RefreshIcon />} sx={{ mt: 3 }}>
            {t('import.continue_import')}
          </Button>
        </Box>
      ) : !preview ? (
        // Upload step
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            id="shopee-upload"
            multiple
            style={{ display: 'none' }}
            onChange={handleUpload}
          />
          <label htmlFor="shopee-upload">
            <Button
              variant="outlined"
              component="span"
              size="large"
              startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
              disabled={uploading}
              sx={{ px: 4, py: 2 }}
            >
              {uploading ? t('import.parsing') : t('import.upload_shopee_excel')}
            </Button>
          </label>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {t('import.file_format_hint')}
          </Typography>
        </Box>
      ) : (
        // Preview & import step
        <>
          {/* Header: file names + detected types */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                {t('import.preview')}: {(preview.file_names || []).join(', ')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('import.valid_selected', { valid: preview.valid_products, selected: selected.length })}
                </Typography>
                {preview.files_detected && Object.entries(FILE_TYPE_LABELS).map(([key, cfg]) => (
                  <Chip
                    key={key}
                    label={`${cfg.label} ${preview.files_detected[key] ? '✓' : '✗'}`}
                    size="small"
                    color={preview.files_detected[key] ? cfg.color : 'default'}
                    variant={preview.files_detected[key] ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>
            </Box>
            <Button variant="outlined" onClick={handleReset}>{t('import.re_upload')}</Button>
          </Box>

          {/* 警告提示 */}
          {preview.warnings && preview.warnings.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {preview.warnings.map((w, i) => <div key={i}>{w}</div>)}
            </Alert>
          )}

          {/* Settings row */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {preview.has_price && (
              <Grid item xs={6} sm={2}>
                <TextField
                  fullWidth type="number" label={t('import.price_adjustment')}
                  value={priceAdjustment}
                  onChange={(e) => setPriceAdjustment(parseFloat(e.target.value) || 1)}
                  InputProps={{ inputProps: { min: 0.5, max: 5, step: 0.1 } }}
                  size="small" helperText={`蝦皮價 ×${priceAdjustment}`}
                />
              </Grid>
            )}
            {!preview.has_price && (
              <Grid item xs={6} sm={2}>
                <TextField
                  fullWidth type="number" label={t('import.default_price')}
                  value={defaultPrice}
                  onChange={(e) => setDefaultPrice(parseFloat(e.target.value) || 0)}
                  InputProps={{ inputProps: { min: 0 } }}
                  size="small" helperText={t('import.apply_when_no_price')}
                />
              </Grid>
            )}
            {!preview.has_stock && (
              <Grid item xs={6} sm={2}>
                <TextField
                  fullWidth type="number" label={t('import.default_stock')}
                  value={defaultStock}
                  onChange={(e) => setDefaultStock(parseInt(e.target.value) || 0)}
                  InputProps={{ inputProps: { min: 0 } }}
                  size="small" helperText={t('import.apply_when_no_stock')}
                />
              </Grid>
            )}
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('import.target_category')}</InputLabel>
                <Select value={targetCategory} onChange={(e) => setTargetCategory(e.target.value)} label={t('import.target_category')}>
                  <MenuItem value="">{t('import.no_specify')}</MenuItem>
                  {categories.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end' }}>
              <Button
                variant="contained" color="success" size="large"
                startIcon={importing ? <CircularProgress size={18} /> : <ImportIcon />}
                onClick={handleImport}
                disabled={importing || selected.length === 0}
              >
                {t('import.import_count', { count: selected.length })}
              </Button>
            </Grid>
          </Grid>

          {importing && <LinearProgress sx={{ mb: 2 }} />}

          {/* Preview table */}
          <TableContainer sx={{ maxHeight: 500 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox checked={selected.length === allProducts.length && allProducts.length > 0} onChange={handleSelectAll} />
                  </TableCell>
                  <TableCell>#</TableCell>
                  {preview.has_images && <TableCell>{t('import.image')}</TableCell>}
                  <TableCell>{t('import.product_name')}</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell align="right">{t('import.selling_price')}</TableCell>
                  <TableCell align="right">{t('import.stock')}</TableCell>
                  {preview.has_images && <TableCell>{t('import.category')}</TableCell>}
                  {preview.has_specs && <TableCell>{t('import.specs')}</TableCell>}
                  <TableCell>{t('import.description')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allProducts.map((p, idx) => (
                  <TableRow key={idx} hover selected={selected.includes(idx)} onClick={() => handleToggle(idx)} sx={{ cursor: 'pointer' }}>
                    <TableCell padding="checkbox">
                      <Checkbox checked={selected.includes(idx)} />
                    </TableCell>
                    <TableCell>{idx + 1}</TableCell>
                    {preview.has_images && (
                      <TableCell>
                        {p.images && p.images.length > 0 ? (
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            {p.images.slice(0, 3).map((url, i) => (
                              <Box key={i} component="img" src={url} alt="" sx={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 0.5, border: '1px solid #ddd' }} />
                            ))}
                            {p.images.length > 3 && (
                              <Typography variant="caption" color="text.secondary">+{p.images.length - 3}</Typography>
                            )}
                          </Box>
                        ) : <ImageIcon color="disabled" fontSize="small" />}
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.name || `(${t('import.no_name')})`}
                      </Typography>
                    </TableCell>
                    <TableCell><Typography variant="caption" color="text.secondary">{p.sku || '-'}</Typography></TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" color={p.price > 0 ? 'error' : 'text.secondary'} variant="body2">
                        ${getDisplayPrice(p)}
                        {p.price <= 0 && defaultPrice > 0 && <Typography variant="caption" display="block" color="text.secondary">({t('import.default')})</Typography>}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color={p.quantity > 0 ? 'text.primary' : 'text.secondary'}>
                        {getDisplayStock(p)}
                        {p.quantity <= 0 && defaultStock > 0 && <Typography variant="caption" display="block" color="text.secondary">({t('import.default')})</Typography>}
                      </Typography>
                    </TableCell>
                    {preview.has_images && (
                      <TableCell>
                        <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                          {p.category || '-'}
                        </Typography>
                      </TableCell>
                    )}
                    {preview.has_specs && (
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', maxWidth: 180 }}>
                          {(p.specs || []).map((s, si) =>
                            s.options?.slice(0, 3).map((opt, oi) => (
                              <Chip key={`${si}-${oi}`} label={opt} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                            ))
                          )}
                          {(p.variations || []).slice(0, 3).map((v, vi) => (
                            <Chip key={`v-${vi}`} label={v.name} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                          ))}
                        </Box>
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                        {p.description ? (p.description.length > 40 ? p.description.substring(0, 40) + '...' : p.description) : '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Paper>
  );
};

export default ImportProducts;
